/**
 * Brand tokens: the colours MUI's palette has no slot for, defined on EVERY theme.
 *
 * Components read these (`theme.palette.brand.*`) instead of writing a hex value, so a brand or
 * mode is restyled here and nowhere else. Each theme supplies the full set, so there is no
 * "is this the TH theme?" fallback anywhere in the app. Plain MUI slots (primary, text, divider,
 * background, success/warning/error/info, grey) remain the first choice where one fits; these are
 * only for what they cannot express.
 */
export interface StatusPair {
  text: string;
  background: string;
}

export interface BrandTokens {
  /** Icons, selected states, focus. */
  accent: string;
  /** Accent at text contrast (≥ 4.5:1 on paper): links and accent-coloured copy. */
  accentStrong: string;
  /** Text on an `accent` fill. */
  onAccent: string;
  /** App bar, left rail and the bars on registration / unauthenticated pages. */
  shell: { background: string; text: string };
  /** Hairline borders on the shell and cards. */
  border: string;
  /** The text wordmark in the app bar: its first word in `lead`, the rest in `rest`. */
  wordmark: { lead: string; rest: string };
  /**
   * Banners, hero headers, feature panels (questionnaire, welcome survey, insurance cards).
   * `muted` is the de-emphasised variant (an optional patient-dashboard card); all take `onHero` text.
   */
  gradient: { hero: string; heroHover: string; alt: string; muted: string };
  /** Text and outlines drawn ON a `gradient`. */
  onHero: string;
  /** Decorative glow behind the app canvas and login (a CSS background-image), or "none". */
  ambient: string;
  /** Tinted panels: plain inset, informational, and accent-tinted. */
  panel: { neutral: string; info: string; accent: string };
  /** Status chip / badge pairs. */
  status: Record<"complete" | "action" | "error" | "info", StatusPair>;
  /** Card resting shadow and a raised one (hover, floating panels). */
  shadow: { card: string; raised: string };
  /** Categorical chart series, fixed order (see DevInsights/chartColors.ts). */
  chart: readonly [string, string, string];
  /** Behind live video and photo previews. */
  mediaStage: string;
  /** Choropleth/bubble map on the interested-users page. */
  map: { land: string; landStroke: string; bubble: string; bubbleStroke: string };
}

declare module "@mui/material/styles" {
  interface Palette {
    brand: BrandTokens;
  }
  interface PaletteOptions {
    brand?: BrandTokens;
  }
}

/** Shared light theme (CerebraLinked light). Built on its teal #52B5AD / blue #2A6D92 palette. */
export const sharedLightBrand: BrandTokens = {
  accent: "#52B5AD",
  accentStrong: "#2A6D92",
  onAccent: "#FFFFFF",
  shell: { background: "#FFFFFF", text: "#1A1A1A" },
  border: "rgba(0, 0, 0, 0.08)",
  wordmark: { lead: "#3A8F88", rest: "#1A1A1A" },
  gradient: {
    hero: "linear-gradient(45deg, #52B5AD 0%, #7BC7C1 100%)",
    heroHover: "linear-gradient(45deg, #3A8F88 0%, #52B5AD 100%)",
    alt: "linear-gradient(135deg, #1C4E6B 0%, #2A6D92 100%)",
    muted: "linear-gradient(135deg, #64748B 0%, #475569 100%)",
  },
  onHero: "#FFFFFF",
  ambient: "none",
  panel: { neutral: "#F1F4F6", info: "#E8F2F7", accent: "#E6F4F3" },
  status: {
    complete: { text: "#0D503C", background: "#E7F6E7" },
    action: { text: "#805D15", background: "#FFF8E7" },
    error: { text: "#A31515", background: "#FEEBEB" },
    info: { text: "#1C4E6B", background: "#E8F2F7" },
  },
  shadow: { card: "0 2px 8px rgba(16, 24, 40, 0.08)", raised: "0 8px 24px rgba(16, 24, 40, 0.15)" },
  chart: ["#2a78d6", "#eb6834", "#1baf7a"],
  mediaStage: "#0A1728",
  map: { land: "#ECEFF1", landStroke: "#CFD8DC", bubble: "rgba(42, 109, 146, 0.55)", bubbleStroke: "#1C4E6B" },
};

/** Shared dark theme (every brand's dark mode): deep navy, CerebraLinked teal, electric blue. */
export const sharedDarkBrand: BrandTokens = {
  accent: "#16BCC0",
  accentStrong: "#22D3D6",
  onAccent: "#04141C",
  shell: { background: "#0A1728", text: "#F4FBFC" },
  border: "rgba(255, 255, 255, 0.08)",
  wordmark: { lead: "#22D3D6", rest: "#F4FBFC" },
  gradient: {
    hero: `radial-gradient(1100px 240px at 0% -30%, rgba(34, 211, 214, 0.18), transparent 55%),
       radial-gradient(900px 300px at 100% 150%, rgba(30, 124, 173, 0.22), transparent 60%),
       linear-gradient(120deg, #11314F 0%, #0C2036 55%, #0A1728 100%)`,
    heroHover: "linear-gradient(120deg, #164066 0%, #11314F 100%)",
    alt: "linear-gradient(120deg, #0D4C6B 0%, #0C2036 100%)",
    muted: "linear-gradient(135deg, #2A3B55 0%, #1B2A40 100%)",
  },
  onHero: "#F4FBFC",
  ambient: `radial-gradient(1100px 640px at 6% -12%, rgba(34, 211, 214, 0.12), transparent 60%),
     radial-gradient(960px 720px at 104% 112%, rgba(30, 124, 173, 0.20), transparent 60%),
     radial-gradient(720px 520px at 92% -4%, rgba(22, 188, 192, 0.08), transparent 60%)`,
  panel: { neutral: "#16273F", info: "#14304C", accent: "#123A40" },
  status: {
    complete: { text: "#4ECCA3", background: "#1B4D3E" },
    action: { text: "#FFC107", background: "#4D3A1B" },
    error: { text: "#FF6B6B", background: "#4D1B1B" },
    info: { text: "#7CC4F0", background: "#123552" },
  },
  shadow: {
    card: "0 24px 60px -28px rgba(0, 0, 0, 0.75), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
    raised: "0 30px 70px -24px rgba(0, 0, 0, 0.75), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
  },
  chart: ["#3987e5", "#d95926", "#199e70"],
  mediaStage: "#050C17",
  map: { land: "#1B2E48", landStroke: "#2A4060", bubble: "rgba(34, 211, 214, 0.45)", bubbleStroke: "#22D3D6" },
};
